import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DateTime } from 'luxon'
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import { FieldLabel } from './Field'
import {
  dateFormat,
  datePlaceholder,
  dateTimeFormat,
  dateTimePlaceholder,
  parseDateInput,
  parseDateTimeInput,
} from './dateTimeInput'
import {
  getCalendarTimeZone,
  getDisplayTimeZone,
  uses24HourClock,
  weekStartWeekday,
} from '../../lib/format'

// Luxon weekday 1 = Monday … 7 = Sunday.
const WEEKDAY_NAMES = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' }
const HOURS = Array.from({ length: 12 }, (_, i) => i + 1)
const HOURS_24 = Array.from({ length: 24 }, (_, i) => i)

/** "Now" in the zone the field reads and writes: the person's display zone
 *  for date + time, the company's calendar for a date-only field. */
const nowInZone = (dateOnly) =>
  DateTime.now().setZone((dateOnly ? getCalendarTimeZone() : getDisplayTimeZone()) || 'local')
const MINUTES = Array.from({ length: 60 }, (_, i) => i)

const inputBase =
  'w-full h-10 pl-3 pr-10 rounded-lg border bg-[color:var(--color-card)] ' +
  'text-[color:var(--color-ink)] text-sm ' +
  'placeholder:text-[color:var(--color-ink-subtle)] placeholder:text-sm ' +
  'outline-none transition-colors focus:border-[color:var(--color-accent)]'

const selectBase =
  'h-8 rounded border border-[color:var(--color-rule)] ' +
  'bg-[color:var(--color-card)] text-sm px-1.5 cursor-pointer ' +
  'outline-none focus:border-[color:var(--color-accent)]'

/**
 * Date + time field in the person's own date format and clock (default
 * `DD/MM/YYYY HH:MM (AM/PM)`), read and written in their display zone. The
 * value is the display string (same shape the placeholder shows) so callers
 * can store it verbatim and convert to ISO on submit via `parseDateTimeInput`.
 * Users can type the value directly OR click the calendar to pick a day
 * plus an hour / minute / meridiem — native pickers can't render this
 * format, so this is a self-contained popover.
 */
export function DateTimePickerField({
  label,
  value,
  onChange,
  error,
  required = false,
  placeholder,
  id = 'datetime-field',
  /** Day-granular mode: renders DD/MM/YYYY with NO time row, and picking a day
   *  commits + closes. Default false keeps the full date + time behaviour. */
  dateOnly = false,
  /** Optional lower bound (Luxon DateTime). Calendar days BEFORE this date's
   *  day are disabled so past dates can't be picked. Opt-in — most fields
   *  accept any date; expiry-style fields pass `DateTime.local()`. */
  minDate = null,
}) {
  const [open, setOpen] = useState(false)
  const fieldRef = useRef(null)
  const popRef = useRef(null)
  const [coords, setCoords] = useState(null)

  const parse = dateOnly ? parseDateInput : parseDateTimeInput
  const fmt = dateOnly ? dateFormat() : dateTimeFormat()
  const ph = placeholder ?? (dateOnly ? datePlaceholder() : dateTimePlaceholder())
  const clock24 = uses24HourClock()

  const parsed = useMemo(
    () => (dateOnly ? parseDateInput : parseDateTimeInput)(value),
    [value, dateOnly],
  )
  const [viewMonth, setViewMonth] = useState(() =>
    (parse(value) ?? nowInZone(dateOnly)).startOf('month'),
  )

  // Opening re-seeds the visible month to the selected date so reopening
  // never strands the user on an unrelated month. Done in the click handler
  // (not an effect) to avoid a setState-in-effect render cascade.
  const toggleOpen = () => {
    if (open) {
      setOpen(false)
      return
    }
    setViewMonth((parse(value) ?? nowInZone(dateOnly)).startOf('month'))
    setOpen(true)
  }

  // The popover is portaled to <body> so the modal's `overflow` can't clip
  // it (that was hiding the calendar). Seed fixed coords under the field.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null)
      return
    }
    const r = fieldRef.current?.getBoundingClientRect()
    if (r) setCoords({ top: r.bottom + 4, left: r.left })
  }, [open])

  // Once measured, nudge up / left so the popover never spills past the
  // viewport edges. Converges via the >1px guard, so no render loop.
  useLayoutEffect(() => {
    if (!open || !coords) return
    const pop = popRef.current
    const r = fieldRef.current?.getBoundingClientRect()
    if (!pop || !r) return
    const margin = 8
    const vh = window.innerHeight
    const vw = window.innerWidth
    let top = r.bottom + 4
    if (top + pop.offsetHeight > vh - margin) {
      const above = r.top - 4 - pop.offsetHeight
      top = above >= margin ? above : Math.max(margin, vh - margin - pop.offsetHeight)
    }
    const left = Math.max(margin, Math.min(r.left, vw - margin - pop.offsetWidth))
    if (Math.abs(top - coords.top) > 1 || Math.abs(left - coords.left) > 1) {
      setCoords({ top, left })
    }
  }, [open, coords])

  // Keep the popover glued to the field while the modal body or window
  // scrolls / resizes. Capture phase catches inner scroll containers.
  useEffect(() => {
    if (!open) return undefined
    const reflow = () => {
      const r = fieldRef.current?.getBoundingClientRect()
      if (r) setCoords({ top: r.bottom + 4, left: r.left })
    }
    window.addEventListener('resize', reflow)
    window.addEventListener('scroll', reflow, true)
    return () => {
      window.removeEventListener('resize', reflow)
      window.removeEventListener('scroll', reflow, true)
    }
  }, [open])

  // Outside-click + Escape close. The popover is portaled, so check the
  // field AND the popover before dismissing.
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (
        !fieldRef.current?.contains(e.target) &&
        !popRef.current?.contains(e.target)
      ) {
        setOpen(false)
      }
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // The DateTime the popover edits: the parsed value, or noon today as a
  // sensible default when nothing valid is entered yet.
  const working =
    parsed ?? nowInZone(dateOnly).set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
  const hour12 = ((working.hour + 11) % 12) + 1
  const meridiem = working.hour < 12 ? 'AM' : 'PM'

  const commit = (dt) =>
    onChange(
      dt
        .set({ second: 0, millisecond: 0 })
        .toFormat(fmt)
        .replace(/\b(am|pm)\b/i, (m) => m.toUpperCase()),
    )

  const pickDay = (day) => {
    if (dateOnly) {
      // Day-granular: commit the date and close — there's no time to pick.
      commit(day.startOf('day'))
      setOpen(false)
      return
    }
    const base =
      parsed ?? nowInZone(dateOnly).set({ hour: 12, minute: 0, second: 0, millisecond: 0 })
    commit(day.set({ hour: base.hour, minute: base.minute }))
  }
  const setHour12 = (h) => {
    const h24 = meridiem === 'PM' ? (h % 12) + 12 : h % 12
    commit(working.set({ hour: h24 }))
  }
  const setMinute = (m) => commit(working.set({ minute: m }))
  const setMeridiem = (mer) => {
    const h = working.hour % 12
    commit(working.set({ hour: mer === 'PM' ? h + 12 : h }))
  }

  // 6-week grid starting on the person's first day of the week (Monday or
  // Sunday), spanning the visible month with days from the neighbours.
  const firstWeekday = weekStartWeekday()
  const weekdays = Array.from({ length: 7 }, (_, i) => WEEKDAY_NAMES[((firstWeekday - 1 + i) % 7) + 1])
  const gridStart = viewMonth.minus({ days: (viewMonth.weekday - firstWeekday + 7) % 7 })
  const days = Array.from({ length: 42 }, (_, i) => gridStart.plus({ days: i }))
  const today = nowInZone(dateOnly).startOf('day')
  const minDay = minDate ? minDate.startOf('day') : null

  const borderClass = error
    ? 'border-[color:var(--color-priority-urgent)]'
    : 'border-[color:var(--color-rule)]'

  return (
    <div>
      <FieldLabel label={label} required={required} htmlFor={id} />
      <div ref={fieldRef} className="relative">
        <input
          id={id}
          type="text"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          className={`${inputBase} ${borderClass}`}
          placeholder={ph}
        />
        <button
          type="button"
          onClick={toggleOpen}
          aria-label="Open calendar"
          aria-haspopup="dialog"
          aria-expanded={open}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded cursor-pointer text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]"
        >
          <CalendarIcon size={16} />
        </button>
      </div>

      {open && coords
        ? createPortal(
            <div
              ref={popRef}
              role="dialog"
              aria-label={`Choose ${typeof label === 'string' ? label : 'date and time'}`}
              style={{ position: 'fixed', top: coords.top, left: coords.left, zIndex: 60 }}
              className="w-72 max-w-[calc(100vw-1rem)] rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] shadow-lg p-3"
            >
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setViewMonth((m) => m.minus({ months: 1 }))}
              className="p-1 rounded cursor-pointer text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)]"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
              {viewMonth.toFormat('MMMM yyyy')}
            </span>
            <button
              type="button"
              aria-label="Next month"
              onClick={() => setViewMonth((m) => m.plus({ months: 1 }))}
              className="p-1 rounded cursor-pointer text-[color:var(--color-ink-muted)] hover:bg-[color:var(--color-card-hover)]"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {weekdays.map((w) => (
              <div
                key={w}
                className="text-center text-[10px] uppercase tracking-wide font-semibold text-[color:var(--color-ink-subtle)] py-1"
              >
                {w}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {days.map((d) => {
              const inMonth = d.month === viewMonth.month
              const isSelected = parsed && d.hasSame(parsed, 'day')
              const isToday = d.hasSame(today, 'day')
              const isDisabled = minDay ? d.startOf('day') < minDay : false
              return (
                <button
                  key={d.toISODate()}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => pickDay(d)}
                  className={
                    'h-8 rounded text-xs transition-colors ' +
                    (isDisabled
                      ? 'text-[color:var(--color-ink-subtle)] opacity-40 cursor-not-allowed'
                      : 'cursor-pointer ' +
                        (isSelected
                          ? 'bg-[color:var(--color-accent)] text-white font-semibold'
                          : inMonth
                            ? 'text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]'
                            : 'text-[color:var(--color-ink-subtle)] hover:bg-[color:var(--color-card-hover)]')) +
                    (isToday && !isSelected && !isDisabled
                      ? ' ring-1 ring-inset ring-[color:var(--color-accent)]'
                      : '')
                  }
                >
                  {d.day}
                </button>
              )
            })}
          </div>

          {!dateOnly ? (
          <div className="mt-3 pt-3 border-t border-[color:var(--color-rule)] flex items-center gap-1.5">
            {clock24 ? (
              <select
                aria-label="Hour"
                value={working.hour}
                onChange={(e) => commit(working.set({ hour: Number(e.target.value) }))}
                className={selectBase}
              >
                {HOURS_24.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}
                  </option>
                ))}
              </select>
            ) : (
              <select
                aria-label="Hour"
                value={hour12}
                onChange={(e) => setHour12(Number(e.target.value))}
                className={selectBase}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}
                  </option>
                ))}
              </select>
            )}
            <span className="text-sm text-[color:var(--color-ink-muted)]">:</span>
            <select
              aria-label="Minute"
              value={working.minute}
              onChange={(e) => setMinute(Number(e.target.value))}
              className={selectBase}
            >
              {MINUTES.map((m) => (
                <option key={m} value={m}>
                  {String(m).padStart(2, '0')}
                </option>
              ))}
            </select>
            {clock24 ? null : (
              <select
                aria-label="AM or PM"
                value={meridiem}
                onChange={(e) => setMeridiem(e.target.value)}
                className={selectBase}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="ml-auto text-xs font-semibold text-[color:var(--color-accent-strong)] hover:underline cursor-pointer"
            >
              Done
            </button>
          </div>
          ) : null}
            </div>,
            document.body,
          )
        : null}

      {error ? (
        <p
          id={`${id}-error`}
          role="alert"
          className="mt-1 text-xs text-[color:var(--color-priority-urgent)]"
        >
          {error}
        </p>
      ) : null}
    </div>
  )
}
